import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Package, 
  Truck, 
  CheckCircle, 
  Clock, 
  MapPin,
  ExternalLink,
  Copy,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status labels
const STATUS_LABELS = {
  pending: { fr: "En attente", color: "bg-zinc-500" },
  processing: { fr: "En traitement", color: "bg-blue-500" },
  sent_to_agent: { fr: "Envoyé à l'agent", color: "bg-purple-500" },
  at_warehouse: { fr: "En entrepôt", color: "bg-indigo-500" },
  qc_pending: { fr: "QC en attente", color: "bg-yellow-500" },
  qc_approved: { fr: "QC approuvé", color: "bg-green-500" },
  shipped: { fr: "Expédié", color: "bg-cyan-500" },
  in_transit: { fr: "En transit", color: "bg-teal-500" },
  delivered: { fr: "Livré", color: "bg-green-600" },
  paid: { fr: "Payé", color: "bg-green-500" },
  cancelled: { fr: "Annulé", color: "bg-red-500" },
};

export function OrderDetailPage() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPackages, setExpandedPackages] = useState({});

  const getToken = () => localStorage.getItem('auth_token');
  const currentLang = i18n.language?.slice(0, 2) || 'fr';

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/api/orders/${orderNumber}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('auth_token');
          navigate('/login');
          return;
        }
        if (response.status === 404) {
          toast.error('Commande non trouvée');
          navigate('/account/orders');
          return;
        }
        throw new Error('Failed to fetch order');
      }

      const data = await response.json();
      setOrder(data);
      
      // Expand first package by default
      if (data.packages?.length > 0) {
        setExpandedPackages({ 1: true });
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Erreur lors du chargement de la commande');
    } finally {
      setLoading(false);
    }
  }, [orderNumber, navigate]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const copyTrackingNumber = (trackingNumber) => {
    navigator.clipboard.writeText(trackingNumber);
    toast.success('Numéro de suivi copié');
  };

  const togglePackage = (packageNumber) => {
    setExpandedPackages(prev => ({
      ...prev,
      [packageNumber]: !prev[packageNumber]
    }));
  };

  const getStatusBadge = (status) => {
    const statusInfo = STATUS_LABELS[status] || STATUS_LABELS.pending;
    return (
      <Badge className={`${statusInfo.color} text-white`}>
        {statusInfo.fr}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatPrice = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const isMultiPackage = order.package_count > 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/account/orders')}
              data-testid="back-orders-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">
                Commande #{order.order_number}
              </h1>
              <p className="text-sm text-zinc-400">
                Passée le {formatDate(order.created_at)}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Multi-package banner */}
        {isMultiPackage && (
          <Card className="bg-primary/10 border-primary/30 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                  <Package className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-white font-medium">
                    Votre commande est expédiée en {order.package_count} colis
                  </p>
                  <p className="text-sm text-zinc-400">
                    Chaque colis a son propre numéro de suivi
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Packages / Shipments */}
        <div className="space-y-4 mb-8">
          {order.packages?.map((pkg, index) => (
            <Card 
              key={pkg.package_number} 
              className="bg-zinc-900/50 border-zinc-800"
              data-testid={`package-card-${pkg.package_number}`}
            >
              <CardHeader 
                className="cursor-pointer"
                onClick={() => togglePackage(pkg.package_number)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center">
                      <Truck className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div>
                      <CardTitle className="text-white text-lg">
                        {isMultiPackage ? `Colis ${pkg.package_number}` : 'Votre colis'}
                      </CardTitle>
                      <p className="text-sm text-zinc-400">
                        {pkg.items?.length || 0} article(s)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(pkg.status)}
                    {expandedPackages[pkg.package_number] ? (
                      <ChevronUp className="w-5 h-5 text-zinc-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-zinc-400" />
                    )}
                  </div>
                </div>
              </CardHeader>

              {expandedPackages[pkg.package_number] && (
                <CardContent className="pt-0">
                  <Separator className="bg-zinc-800 mb-4" />

                  {/* Tracking info */}
                  {pkg.tracking_number ? (
                    <div className="bg-zinc-800/50 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-zinc-400">Numéro de suivi</p>
                          <p className="text-white font-mono">{pkg.tracking_number}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => copyTrackingNumber(pkg.tracking_number)}
                            className="text-zinc-400 hover:text-white"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          {pkg.tracking_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(pkg.tracking_url, '_blank')}
                              className="border-zinc-700"
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Suivre
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-zinc-800/50 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-2 text-zinc-400">
                        <Clock className="w-4 h-4" />
                        <p className="text-sm">Numéro de suivi en attente</p>
                      </div>
                    </div>
                  )}

                  {/* Items in this package */}
                  <div className="space-y-3">
                    {pkg.items?.map((item, itemIndex) => (
                      <div 
                        key={itemIndex}
                        className="flex items-center gap-4 p-3 bg-zinc-800/30 rounded-lg"
                      >
                        <div className="w-16 h-16 bg-zinc-800 rounded-lg overflow-hidden flex-shrink-0">
                          {item.image && (
                            <img 
                              src={item.image} 
                              alt={item.name?.[currentLang] || item.name?.fr}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">
                            {item.name?.[currentLang] || item.name?.fr || item.name}
                          </p>
                          <p className="text-sm text-zinc-400">
                            {item.variant?.attributes?.color && (
                              <span>{item.variant.attributes.color} • </span>
                            )}
                            {item.variant?.attributes?.size && (
                              <span>Taille {item.variant.attributes.size}</span>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-white font-medium">
                            {formatPrice(item.price, order.pricing?.currency)}
                          </p>
                          <p className="text-sm text-zinc-400">
                            Qté: {item.quantity}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* QC Photos if available */}
                  {pkg.qc_photos?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-zinc-400 mb-2">Photos de contrôle qualité</p>
                      <div className="flex gap-2 overflow-x-auto pb-2">
                        {pkg.qc_photos.map((photo, photoIndex) => (
                          <div 
                            key={photoIndex}
                            className="w-20 h-20 bg-zinc-800 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer"
                            onClick={() => window.open(photo, '_blank')}
                          >
                            <img 
                              src={photo} 
                              alt={`QC ${photoIndex + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* Order Summary */}
        <Card className="bg-zinc-900/50 border-zinc-800 mb-8">
          <CardHeader>
            <CardTitle className="text-white">Récapitulatif</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between text-zinc-400">
                <span>Sous-total</span>
                <span>{formatPrice(order.pricing?.subtotal, order.pricing?.currency)}</span>
              </div>
              <div className="flex justify-between text-zinc-400">
                <span>Livraison</span>
                <span>
                  {order.pricing?.shipping === 0 
                    ? 'Gratuit' 
                    : formatPrice(order.pricing?.shipping, order.pricing?.currency)
                  }
                </span>
              </div>
              {order.pricing?.discount > 0 && (
                <div className="flex justify-between text-green-400">
                  <span>Réduction</span>
                  <span>-{formatPrice(order.pricing?.discount, order.pricing?.currency)}</span>
                </div>
              )}
              <Separator className="bg-zinc-800" />
              <div className="flex justify-between text-white text-lg font-bold">
                <span>Total</span>
                <span>{formatPrice(order.pricing?.total, order.pricing?.currency)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Shipping Address */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Adresse de livraison
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-zinc-300">
              <p className="font-medium">{order.shipping?.address?.first_name} {order.shipping?.address?.last_name}</p>
              <p>{order.shipping?.address?.address_line1}</p>
              {order.shipping?.address?.address_line2 && (
                <p>{order.shipping?.address?.address_line2}</p>
              )}
              <p>{order.shipping?.address?.postal_code} {order.shipping?.address?.city}</p>
              <p>{order.shipping?.address?.country}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default OrderDetailPage;
